import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import { EServerStyle, IChatUser, IMessage } from '~/types/common';
import { formatDate } from '~/utils/formatDate';
import { MenuActionIcon, UserPlusIcon } from '../Icons/Icons';
import { usePrivateMessageClick } from '~/hooks/usePrivateMessageClick';
import { openAvatarLightbox } from '~/components/AvatarLightbox/openAvatarLightbox';
import gifData from '~/data/gifs.json';
import classNames from 'classnames';
import styles from './Message.module.css';
import MessageReactions from './MessageReactions';

interface GifItem {
  id: number;
  url: string;
}

interface MessageProps {
  message: IMessage;
  uniqueKey: number | string;
}

const Message = ({ message, uniqueKey = 0 }: MessageProps) => {
  const { context, setContext } = useGlobalContext();
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number; left: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const formattedTime = formatDate(message.serverTime);

  const handlePrivateMessageClick = usePrivateMessageClick();

  const isAdmin = context.authUser?.role === 'administrator';
  const isModerator = isAdmin || context.authUser?.role === 'moderator';

  // Funkcja sprawdzająca czy kontener jest skrolowalny i gdzie jest scroll
  const handleScroll = () => {
    const el = scrollRef.current;
    if (el) {
      const { scrollTop, scrollHeight, clientHeight } = el;

      // Czy jest co skrolować?
      const isScrollable = scrollHeight > clientHeight;

      if (isScrollable) {
        // Pokaż górny cień, jeśli nie jesteśmy na samej górze
        setShowTopShadow(scrollTop > 5);
        // Pokaż dolny cień, jeśli nie jesteśmy na samym dole
        setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 5);
      } else {
        setShowTopShadow(false);
        setShowBottomShadow(false);
      }
    }
  };

  // Sprawdź stan przy montowaniu i zmianie treści (np. po załadowaniu obrazków)
  useEffect(() => {
    handleScroll();
    // Reaguj na zmiany rozmiaru okna
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, [message.userText, message.serverText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const isInsideDropdown = dropdownRef.current?.contains(target);
      const isToggleButton = toggleRef.current?.contains(target);
      if (!isInsideDropdown && !isToggleButton) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleToggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDropdownOpen && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      const estimatedMenuHeight = message.serverStyle ? 60 : isModerator ? 260 : 100;
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropdownCoords(
        spaceBelow >= estimatedMenuHeight
          ? { top: rect.bottom + 2, left: rect.left }
          : { top: rect.top - estimatedMenuHeight - 2, left: rect.left }
      );
    }
    setIsDropdownOpen(prev => !prev);
  };

  const extractId = (tag: string, type: 'em' | 'gif') => {
    const regex = type === 'em' ? /<em\/(\d+)>/ : /<gif\/(\d+)>/;
    const match = tag.match(regex);
    return match ? match[1] : null;
  };

  const renderEmbed = (url: string, index: number) => {
    let embedUrl = '';
    let ratioClass = styles.ratio16_9;
    const ytMatch = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
    else if (url.includes('tiktok.com')) {
      const ttIdMatch = url.match(/\/video\/(\d+)/);
      if (ttIdMatch) {
        embedUrl = `https://www.tiktok.com/embed/v2/${ttIdMatch[1]}`;
        ratioClass = styles.ratio9_16;
      }
    } else if (url.match(/instagram\.com\/(?:p|reels|reel)\/([\w-]+)/)) {
      const igMatch = url.match(/\/(?:p|reels|reel)\/([\w-]+)/);
      if (igMatch) {
        embedUrl = `https://www.instagram.com/p/${igMatch[1]}/embed`;
        ratioClass = styles.ratio9_16;
      }
    } else if (url.includes('facebook.com')) {
      embedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=0&t=0`;
      if (url.includes('/reels/') || url.includes('/watch')) ratioClass = styles.ratio9_16;
    }

    if (embedUrl) {
      return (
        <div key={`embed-container-${index}`} className={styles.embedContainer}>
          <div className={classNames(styles.embedWrapper, ratioClass)}>
            <iframe
              src={embedUrl}
              allowFullScreen
              title={`embed-${index}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            />
          </div>
          <div className={styles.embedLinkFooter}>
            <span className={styles.embedLinkText}>{url}</span>
            <button onClick={() => navigator.clipboard.writeText(url)} className={styles.copyButton}>Kopiuj</button>
          </div>
        </div>
      );
    }
    return (
      <a key={`link-${index}`} href={url} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
        {url}
      </a>
    );
  };

  const parseMessageText = (text: string) => {
    if (!text) return "";

    // Dodano \B@\S+ do regexa, aby wyłapywał oznaczenia (mentions) zaczynające się od @
    const combinedRegex = /(<em\/\d+>|<gif\/\d+>|https?:\/\/[^\s]+|\B@\S+)/g;

    return text.split(combinedRegex).map((part, index) => {
      if (!part) return null;

      // 1. Emotikony
      if (/<em\/\d+>/.test(part)) {
        const id = extractId(part, 'em');
        return id ? <img key={`em-${index}`} src={`/img/emoticons/${id}.png`} alt="" className={styles.mediaEmote} onLoad={handleScroll} /> : part;
      }

      // 2. GIFy
      if (/<gif\/\d+>/.test(part)) {
        const id = extractId(part, 'gif');
        const gifInfo = (gifData as GifItem[]).find(g => String(g.id) === id);
        return id ? <img key={`gif-${index}`} src={`/img/gifs/gifs_elements/${gifInfo ? gifInfo.url : `${id}.gif`}`} alt="" className={styles.mediaGif} onLoad={handleScroll} /> : part;
      }

      // 3. Linki i Embedy
      if (part.startsWith('http')) {
        return renderEmbed(part, index);
      }

      // 4. Oznaczenia Użytkowników (@User)
      if (part.startsWith('@')) {
        return <span key={`mention-${index}`} className={styles.mention}>{part}</span>;
      }

      // 5. Zwykły tekst
      return <span data-message="message" key={`text-part-${index}`}>{part}</span>;
    });
  };

  const renderMessageContent = () => {
    // 1. Sprawdzamy czy wiadomość jest zmoderowana (na podstawie tekstu lub flagi)
    const isModerated = message.isModerated;

    if (isModerated) {
      return (
        <span className={styles.moderatedText}>
          {message.userText}
        </span>
      );
    }

    // 2. Jeśli to wiadomość systemowa (serwerowa)
    if (message.serverText) {
      return message.serverText;
    }

    // 3. Domyślny parser (emotki, gify, linki)
    return parseMessageText(message.userText ?? "");
  };

  return (
    <div
      id={message.id}
      data-component="Message"
      key={uniqueKey}
      className={classNames(styles.message, {
        [styles.isMine]: message.isMine,
        [styles.isJoin]: EServerStyle.join === message.serverStyle,
        [styles.isLeft]: EServerStyle.left === message.serverStyle,
        [styles.isNotActive]: EServerStyle.notActive === message.serverStyle,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.messageContent}>

        {message.serverStyle ? (
          <div className={styles.userHeaderWrapper}>
            <div className={styles.isSystem}>
              <div className={styles.systemFirstName}>Czateria</div>
              <div className={styles.systemNextName}>24</div>
            </div>

            {isAdmin && (
              <button
                ref={toggleRef}
                className={styles.dropdownToggle}
                onClick={handleToggleDropdown}
                title="Opcje" >
                <MenuActionIcon className={styles.menuActionIcon} />
              </button>
            )}
          </div>
        ) : (
          <>
          <div className={styles.userHeaderWrapper}>
            {!!message.userName && (
              <button
                type="button"
                className={styles.userAvatar}
                title={`Pokaż zdjęcia: ${message.userName}`}
                onClick={(e) => {
                  e.stopPropagation();
                  openAvatarLightbox({ userName: message.userName, avatarUrl: message.userAvatar });
                }}
              >
                {message.userAvatar ? (
                  <img className={styles.userAvatarImg} src={message.userAvatar} alt={message.userName} />
                ) : (
                  <span>{(message.userName || '?').charAt(0)}</span>
                )}
              </button>
            )}
            <div className={styles.messageUser}>
              {message.userName}
            </div>

            {!message.isMine && (
              <button
                ref={toggleRef}
                className={styles.dropdownToggle}
                onClick={handleToggleDropdown}
                title="Opcje" >
                <MenuActionIcon className={styles.menuActionIcon} />
              </button>
            )}
          </div>

          </>
        )}

        {isDropdownOpen && dropdownCoords && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdownMenuPortal}
            style={{ top: dropdownCoords.top, left: dropdownCoords.left }}
          >
            {message.serverStyle ? (
              // Powiadomienie systemowe — tylko administrator może je usunąć
              isAdmin && (
                <button
                  className={classNames(styles.dropdownItem, styles.dropdownItemDanger)}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('deleteMessage', {
                      detail: {
                        user: context.user,
                        message: message,
                        room: context.room
                      }
                    }));
                    setIsDropdownOpen(false);
                  }}>
                  <div className={styles.dropdownItemIcon}>🗑️</div>
                  <div className={styles.dropdownItemText}>Usuń powiadomienie</div>
                </button>
              )
            ) : (
              <>
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    const targetUser = { id: message.userId, name: message.userName } as IChatUser;
                    const currentUserId = context.user?.id || '';
                    handlePrivateMessageClick(targetUser, currentUserId, () => setIsDropdownOpen(false));
                  }} >
                  <div className={styles.dropdownItemIcon}>📩</div>
                  <div className={styles.dropdownItemText}>Wiadomość</div>
                </button>

                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('replyToUser', {
                      detail: message
                    }));
                    setIsDropdownOpen(false);
                  }}>
                  <div className={styles.dropdownItemIcon}>↩️</div>
                  <div className={styles.dropdownItemText}>Odpisz</div>
                </button>

                {isModerator && (
                  <>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('deleteMessage', {
                          detail: {
                            user: context.user,
                            message: message,
                            room: context.room
                          }
                        }));
                        setIsDropdownOpen(false);
                      }}>
                      <div className={styles.dropdownItemIcon}>🗑️</div>
                      <div className={styles.dropdownItemText}>Usuń wiadomość</div>
                    </button>

                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('hideMessage', {
                          detail: {
                            user: context.user,
                            message: message,
                            room: context.room
                          }
                        }));
                        setIsDropdownOpen(false);
                      }}>
                      <div className={styles.dropdownItemIcon}>🔒</div>
                      <div className={styles.dropdownItemText}>Ukryj treść wiadomości</div>
                    </button>

                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('banWarning', {
                          detail: {
                            targetUserId: message.userId,
                            targetUserName: message.userName,
                          }
                        }));
                        setIsDropdownOpen(false);
                      }}>
                      <div className={styles.dropdownItemIcon}>⚠️</div>
                      <div className={styles.dropdownItemText}>Ostrzeż o banie</div>
                    </button>

                    {message.userRole !== 'administrator' && (
                      <>
                        <button
                          className={styles.dropdownItem}
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('kickUser', {
                              detail: {
                                targetUserId: message.userId,
                                targetUserName: message.userName,
                              }
                            }));
                            setIsDropdownOpen(false);
                          }}>
                          <div className={styles.dropdownItemIcon}>🚪</div>
                          <div className={styles.dropdownItemText}>Wyloguj</div>
                        </button>

                        <button
                          className={classNames(styles.dropdownItem, styles.dropdownItemDanger)}
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('banUser', {
                              detail: {
                                targetUserId: message.userId,
                                targetUserName: message.userName,
                              }
                            }));
                            setIsDropdownOpen(false);
                          }}>
                          <div className={styles.dropdownItemIcon}>🚫</div>
                          <div className={styles.dropdownItemText}>Zablokuj możliwość logowania</div>
                        </button>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>,
          document.body
        )}

        <div className={styles.scrollWrapper}>
          <div
            className={classNames(styles.shadow, styles.shadowTop, { [styles.showShadow]: showTopShadow })}
          />
          <div
            className={styles.messageText}
            ref={scrollRef}
            onScroll={handleScroll}>
            {/* {message.serverText ? message.serverText : parseMessageText(message.userText ?? "")} */}
            {renderMessageContent()}
          </div>

          <div
            className={classNames(styles.shadow, styles.shadowBottom, { [styles.showShadow]: showBottomShadow })}></div>
        </div>

        <div className={styles.messageTime}>{formattedTime}</div>

        {!message.serverStyle && (
          <MessageReactions
            messageId={message.id}
            roomId={message.roomId}
            reactions={message.reactions}
            isMine={message.isMine}
            isHovered={isHovered}
          />
        )}
      </div>
    </div>
  );
};

export default Message;